// Ficha de lo elegido en el mapa o en la lista: el detalle y lo que se puede
// hacer desde acá (ver el recorrido del chofer, llamarlo, abrir el recorrido).
import React from "react";
import { Chofer, FUENTE, haceCuanto, nombreChofer, soloDigitos } from "../../lib/liveDrivers";
import { DEFAULT_FILTERS, driverStops, homeName, MapHome, MapLayersData, MapSel, MapStop, rowsFor, STATE_LABEL, turboChofer, TURBO_ESTADO_LABEL, TurboHoy } from "../../lib/mapLayers";
import { accionStyle, cerrarStyle, fichaStyle, smallMutedStyle } from "./styles";

type Props = {
  sel: MapSel;
  foco: number | null;
  capas: MapLayersData | null;
  stops: MapStop[];
  choferes: Chofer[];
  homes: MapHome[];
  turbos?: TurboHoy[];
  choferesConParadas: Set<number>;
  onVerRecorrido: (driverId: number) => void;
  // Navegación dentro del panel (sin recargar la aplicación).
  onIr: (href: string) => void;
  onCerrar: () => void;
};

// Un clic común navega adentro del panel; con Ctrl/Cmd o la ruedita, el
// navegador hace lo suyo (abrirlo en otra pestaña).
const clicComun = (e: React.MouseEvent) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

export default function Ficha({ sel, foco, capas, stops, choferes, homes, turbos = [], choferesConParadas, onVerRecorrido, onIr, onCerrar }: Props) {
  let titulo = "", lineas: string[] = [];
  let acciones: React.ReactNode = null;
  const botonFoco = (driverId: number | null | undefined, texto = "Ver su recorrido") =>
    driverId && foco !== driverId ? <button type="button" style={accionStyle} onClick={() => onVerRecorrido(driverId)}>{texto}</button> : null;
  const telefono = (driverId: number | null | undefined) => {
    const tel = driverId ? choferes.find((c) => c.driver_id === driverId)?.phone ?? null : null;
    return tel ? <a style={{ ...accionStyle, textDecoration: "underline" }} href={`tel:${soloDigitos(tel)}`}>Llamar al chofer</a> : null;
  };
  if (sel.kind === "pickups" || sel.kind === "deliveries") {
    const s = stops.find((x) => x.id === sel.id);
    if (!s) return null;
    titulo = `${s.type === "PICKUP" ? "Colecta" : "Entrega"} · ${s.label || "sin nombre"}`;
    lineas = [
      s.address || "sin dirección",
      `Recorrido #${s.route_id}, parada ${s.sequence} · ${s.driver_id ? s.driver_name || s.driver_username : "sin chofer"}`,
      `${STATE_LABEL[s.state].replace(/s$/, "")}${s.packages ? ` · ${s.packages} paquete${s.packages === 1 ? "" : "s"}` : ""}${s.problems ? ` · ${s.problems} con problema` : ""}`,
    ];
    const href = `/admin/recorridos?date=${capas?.date ?? ""}&recorrido=${s.route_id}`;
    acciones = (
      <>
        <a style={{ ...accionStyle, textDecoration: "underline" }} href={href} onClick={(e) => { if (clicComun(e)) { e.preventDefault(); onIr(href); } }}>Abrir el recorrido</a>
        {botonFoco(s.driver_id, "Ver el recorrido del chofer")}
        {telefono(s.driver_id)}
      </>
    );
  } else if (sel.kind === "drivers") {
    const c = choferes.find((x) => x.driver_id === sel.id);
    if (!c) return null;
    titulo = nombreChofer(c);
    lineas = [`${c.username}${c.vehiculo ? ` · ${c.vehiculo}` : ""}`, `${haceCuanto(c.recorded_at)} · ${FUENTE[c.source] ?? c.source}`,
      choferesConParadas.has(c.driver_id) ? `${driverStops(stops, c.driver_id).length} paradas hoy` : "sin paradas hoy"];
    acciones = <>{choferesConParadas.has(c.driver_id) ? botonFoco(c.driver_id) : null}{telefono(c.driver_id)}</>;
  } else if (sel.kind === "turbo") {
    const t = turbos.find((x) => x.delivery_id === sel.id);
    if (!t) return null;
    const hora = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "");
    titulo = `Turbo ${t.tracking ?? `#${t.delivery_id}`} · ${t.store_name ?? "tienda"} → ${t.nombre_destinatario ?? "destino"}`;
    lineas = [
      [t.direccion, t.localidad].filter(Boolean).join(", ") || "sin dirección",
      `${TURBO_ESTADO_LABEL[t.estado]}${t.estado_at ? ` ${hora(t.estado_at)}` : ""}${t.detalle ? ` · ${t.detalle}` : ""}`,
      `${turboChofer(t)}${t.recorrido ? ` · recorrido #${t.recorrido.route_id}` : ""}${t.vence_at ? ` · entregar antes de ${hora(t.vence_at)}` : ""}`,
    ];
    const href = "/admin/turbo/hoy";
    acciones = (
      <>
        <a style={{ ...accionStyle, textDecoration: "underline" }} href={href} onClick={(e) => { if (clicComun(e)) { e.preventDefault(); onIr(href); } }}>Ver en Turbo · Hoy en vivo</a>
        {t.chofer && choferesConParadas.has(t.chofer.driver_id) ? botonFoco(t.chofer.driver_id, "Ver el recorrido del chofer") : null}
        {telefono(t.chofer?.driver_id)}
      </>
    );
  } else if (sel.kind === "homes") {
    const h = homes.find((x) => x.driver_id === sel.id);
    if (!h) return null;
    titulo = `Domicilio de ${homeName(h)}`;
    lineas = [h.address];
    acciones = <>{choferesConParadas.has(h.driver_id) ? botonFoco(h.driver_id) : null}{telefono(h.driver_id)}</>;
  } else {
    const fila = rowsFor(sel.kind, capas, homes, DEFAULT_FILTERS).find((r) => r.id === sel.id);
    if (!fila) return null;
    const tipo = sel.kind === "warehouses" && capas?.warehouses.find((w) => w.id === sel.id)?.type === "DROPOFF" ? "Dropoff"
      : sel.kind === "warehouses" ? "Depósito" : sel.kind === "stores" ? "Tienda" : "Zona";
    titulo = `${tipo} · ${fila.title}`;
    lineas = [fila.subtitle];
  }
  return (
    <div style={fichaStyle} role="region" aria-label="Elegido">
      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
        <strong style={{ fontSize: "14px", color: "var(--text-primary)" }}>{titulo}</strong>
        <button type="button" aria-label="Cerrar ficha" onClick={onCerrar} style={{ ...cerrarStyle, fontSize: "16px" }}>×</button>
      </div>
      {lineas.filter(Boolean).map((l, i) => <div key={i} style={smallMutedStyle}>{l}</div>)}
      {acciones ? <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "6px" }}>{acciones}</div> : null}
    </div>
  );
}
